import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { fmtDateTime, fmtDate } from "../lib/format";
import {
  ShieldCheck, Download, Trash2, FileText, AlertTriangle, Key,
  CheckCircle2, Send, Clock, Loader2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "mandates",  label: "Mandates" },
  { id: "retention", label: "Data retention" },
  { id: "consents", label: "Consent log" },
  { id: "erasure", label: "Erasure requests" },
  { id: "export", label: "Data export" },
];

const RETENTION = [
  { type: "Personal data", years: 5, basis: "GDPR Article 5(1)(e) — purpose limitation post-contract" },
  { type: "Financial records", years: 7, basis: "National tax law (default 7-year retention)" },
  { type: "Legal documents", years: 10, basis: "Statute of limitations / arbitration claims" },
  { type: "Communications (email/SMS/WA)", years: 3, basis: "Operational record · client account history" },
];

export default function Compliance() {
  const [tab, setTab] = useState("mandates");
  return (
    <Layout title="Compliance" subtitle="Mandates · GDPR · data framework · audit">
      <div className="border-b border-slate-200 flex items-center gap-1 mb-6">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`gdpr-tab-${t.id}`}
            className={`px-4 py-3 text-sm font-bold tracking-tight border-b-2 transition-all ${
              tab === t.id ? "border-emerald-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>{t.label}</button>
        ))}
      </div>
      {tab === "mandates" && <Mandates />}
      {tab === "retention" && <Retention />}
      {tab === "consents" && <Consents />}
      {tab === "erasure" && <Erasure />}
      {tab === "export" && <Export />}
    </Layout>
  );
}

function Mandates() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/compliance/overview");
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load compliance overview");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const runExpirySweep = async () => {
    setRunning(true);
    try { await api.post("/compliance/run-expiry-check"); await load(); }
    catch (e) { setError(e?.response?.data?.detail || "Could not run sweep"); }
    finally { setRunning(false); }
  };

  const downloadRegister = async () => {
    const token = localStorage.getItem("pm_token");
    const r = await fetch(`${API}/compliance/mandate-register/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return;
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `mandate-register.pdf`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  if (loading) return <div className="pm-card p-8 text-center text-slate-500" data-testid="mandates-loading"><Loader2 size={14} className="inline animate-spin mr-2" /> Loading compliance overview…</div>;
  if (error) return <div className="pm-card p-6 text-rose-700 flex items-center gap-2"><AlertTriangle size={14} /> {error}</div>;
  if (!data) return null;

  const c = data.counts || {};

  return (
    <div className="space-y-6" data-testid="mandates-compliance-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1">
          <ShieldCheck size={18} className="text-emerald-600" />
          <h3 className="font-display text-xl font-bold tracking-tight">Mandate compliance</h3>
        </div>
        <button onClick={runExpirySweep} disabled={running}
          className="pm-btn-secondary h-9 px-3 rounded-lg text-[12px] font-bold flex items-center gap-1.5"
          data-testid="mandates-run-expiry-btn">
          {running ? <Loader2 size={11} className="animate-spin" /> : <Clock size={11} />}
          Run expiry check
        </button>
        <button onClick={downloadRegister}
          className="pm-btn-primary h-9 px-3 rounded-lg text-[12px] font-bold flex items-center gap-1.5"
          data-testid="mandates-download-register-btn">
          <Download size={11} /> Export register (PDF)
        </button>
      </div>

      {data.last_expiry_check && (
        <div className="text-[11px] text-slate-500">
          Last expiry sweep: {fmtDateTime(data.last_expiry_check)} ·
          {" "}<b>{data.last_expiry_summary?.expired || 0}</b> auto-expired,
          {" "}<b>{data.last_expiry_summary?.alerted || 0}</b> reminders sent
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <StatCard label="Active" value={c.active} tone="emerald" testid="stat-active" />
        <StatCard label="Expiring ≤30d" value={c.expiring_soon} tone="amber" testid="stat-expiring" />
        <StatCard label="Expired" value={c.expired} tone="rose" testid="stat-expired" />
        <StatCard label="Missing PDF" value={c.no_document} tone="amber" testid="stat-no-doc" />
        <StatCard label="Not verified" value={c.not_verified} tone="slate" testid="stat-not-verified" />
        <StatCard label="Not filed" value={c.not_filed} tone="slate" testid="stat-not-filed" />
      </div>

      {!data.resend_configured && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] text-amber-900 flex items-start gap-2" data-testid="resend-banner">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <div>
            <b>Email submissions to FAs are disabled.</b> Paste a Resend API key in <code>backend/.env</code> as <code>RESEND_API_KEY</code> and a verified <code>SENDER_EMAIL</code> to enable one-click filing to KNVB, FIGC, RFEF, The FA, UEFA. PDF download already works.
          </div>
        </div>
      )}

      <RiskList
        title="Expiring within 30 days"
        items={data.expiring_soon}
        emptyMsg="No mandates expiring soon."
        navigate={navigate}
        accent="amber"
        testid="list-expiring"
      />
      <RiskList
        title="Recently expired"
        items={data.expired_recent}
        emptyMsg="No expired mandates on file."
        navigate={navigate}
        accent="rose"
        testid="list-expired"
      />
      <RiskList
        title="Missing signed document"
        items={data.no_document}
        emptyMsg="All active mandates have a signed document on file."
        navigate={navigate}
        accent="amber"
        testid="list-no-doc"
      />
      <RiskList
        title="Pending FIFA registry verification"
        items={data.not_verified}
        emptyMsg="All active mandates have been FIFA-verified."
        navigate={navigate}
        accent="slate"
        testid="list-not-verified"
      />
    </div>
  );
}

function StatCard({ label, value, tone, testid }) {
  const TONES = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    rose:    "bg-rose-50 text-rose-700 border-rose-200",
    slate:   "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <div className={`rounded-xl border px-3 py-3 ${TONES[tone] || TONES.slate}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-70">{label}</div>
      <div className="font-display text-3xl font-black tracking-tight mt-1">{value ?? "—"}</div>
    </div>
  );
}

function RiskList({ title, items, emptyMsg, navigate, accent, testid }) {
  return (
    <div className="pm-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Key size={14} className="text-slate-500" />
        <h4 className="font-bold tracking-tight text-slate-800">{title}</h4>
        <span className="pm-pill pm-pill-slate ml-auto">{items?.length || 0}</span>
      </div>
      {(!items || items.length === 0) ? (
        <div className="text-[12px] text-slate-500">{emptyMsg}</div>
      ) : (
        <div className="space-y-1">
          {items.map((m) => (
            <button
              key={m.id} onClick={() => navigate("/mandates")}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 text-left transition-all"
              data-testid={`risk-row-${m.id}`}
            >
              <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                <Key size={13} className="text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold truncate">
                  {m.client?.full_name || "—"} <span className="text-slate-400 font-normal">·</span> {m.partner_agent_name}
                </div>
                <div className="text-[11px] text-slate-500">
                  {(m.direction || "").toUpperCase()} · {(m.scope_type || "—")} · expires {fmtDate(m.expiry_date)}
                </div>
              </div>
              {m.fifa_verified && (
                <span className="pm-pill bg-emerald-50 text-emerald-800 border border-emerald-200 shrink-0">
                  <CheckCircle2 size={9} className="inline mr-1" /> Verified
                </span>
              )}
              {m.fa_submission && (
                <span className="pm-pill bg-blue-50 text-blue-700 border border-blue-200 shrink-0">
                  <Send size={9} className="inline mr-1" /> Filed
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Retention() {
  return (
    <div className="pm-card p-6" data-testid="retention-panel">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck size={16} className="text-emerald-600" />
        <h3 className="font-display text-xl font-bold tracking-tight">Data retention policy</h3>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        Records are auto-archived at the end of their retention window. Admins receive a review notification 30 days
        before deletion. All deletions are written to an immutable audit log.
      </p>
      <div className="space-y-2">
        {RETENTION.map((r) => (
          <div key={r.type} className="flex items-center gap-4 px-4 py-3 rounded-xl border border-slate-200">
            <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center font-display font-black">{r.years}y</div>
            <div className="flex-1">
              <div className="font-bold text-slate-900">{r.type}</div>
              <div className="text-xs text-slate-500">{r.basis}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Consents() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/gdpr/consents").then((r) => setItems(r.data || [])); }, []);
  return (
    <div className="pm-card p-6" data-testid="consents-panel">
      <h3 className="font-display text-xl font-bold tracking-tight mb-4">Consent records</h3>
      <div className="space-y-2">
        {items.length === 0 && <div className="text-sm text-slate-500">No consent records yet.</div>}
        {items.map((c) => (
          <div key={c.id} className="flex items-center gap-4 px-4 py-3 rounded-xl border border-slate-200" data-testid={`consent-${c.id}`}>
            <div className="flex-1">
              <div className="font-bold text-slate-900">{c.subject_name}</div>
              <div className="text-xs text-slate-500">{c.data_types.join(" · ")} · basis: <b>{c.legal_basis}</b></div>
            </div>
            <div className="text-xs text-slate-500 font-mono-tab">Given {fmtDateTime(c.given_at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Erasure() {
  const [requests, setRequests] = useState([]);
  const [subjectId, setSubjectId] = useState("");
  const [subjectName, setSubjectName] = useState("");
  const load = () => api.get("/gdpr/erasure-requests").then((r) => setRequests(r.data || []));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    if (!subjectId || !subjectName) return;
    await api.post("/gdpr/erasure-request", { subject_id: subjectId, subject_name: subjectName });
    setSubjectId(""); setSubjectName(""); load();
  };
  return (
    <div className="pm-card p-6" data-testid="erasure-panel">
      <h3 className="font-display text-xl font-bold tracking-tight mb-3">Right-to-erasure requests</h3>
      <p className="text-sm text-slate-500 mb-4">
        Submitted requests are queued for admin review. Approved erasures replace personal data with anonymised
        placeholders while preserving accounting and audit integrity.
      </p>
      <div className="rounded-xl border border-slate-200 p-4 bg-slate-50 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input className="pm-input" placeholder="Subject ID (client/contact)" value={subjectId} onChange={(e) => setSubjectId(e.target.value)}
                 data-testid="erasure-subject-id" />
          <input className="pm-input" placeholder="Subject name" value={subjectName} onChange={(e) => setSubjectName(e.target.value)}
                 data-testid="erasure-subject-name" />
          <button onClick={submit} className="pm-btn-primary h-11 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
                  data-testid="erasure-submit">
            <Trash2 size={14} /> Submit request
          </button>
        </div>
      </div>
      <div className="space-y-2">
        {requests.length === 0 && <div className="text-sm text-slate-500">No erasure requests on file.</div>}
        {requests.map((r) => (
          <div key={r.id} className="flex items-center gap-4 px-4 py-3 rounded-xl border border-slate-200" data-testid={`erasure-${r.id}`}>
            <div className="flex-1">
              <div className="font-bold text-slate-900">{r.subject_name}</div>
              <div className="text-xs text-slate-500">{r.subject_id} · requested by {r.requested_by}</div>
            </div>
            <span className="pm-pill pm-pill-amber">{r.status.replace("_", " ")}</span>
            <div className="text-xs text-slate-500 font-mono-tab">{fmtDate(r.created_at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Export() {
  const [clients, setClients] = useState([]);
  const [exportData, setExportData] = useState(null);
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data || [])); }, []);
  const run = async (id) => {
    const { data } = await api.get(`/gdpr/export/${id}`);
    setExportData(data);
  };
  const download = () => {
    if (!exportData) return;
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `playmaker-export-${exportData.subject.id}.json`;
    a.click(); URL.revokeObjectURL(url);
  };
  return (
    <div className="pm-card p-6" data-testid="export-panel">
      <h3 className="font-display text-xl font-bold tracking-tight mb-3">GDPR Article 20 — Data portability</h3>
      <p className="text-sm text-slate-500 mb-4">
        Generate a complete machine-readable export of a client's personal data and history.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
        {clients.map((c) => (
          <button key={c.id} onClick={() => run(c.id)} className="flex items-center gap-3 px-3 py-2 rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 transition-all text-left"
                  data-testid={`export-${c.id}`}>
            <img src={c.photo_url} alt="" className="w-8 h-8 rounded-lg object-cover" />
            <span className="font-bold text-sm">{c.full_name}</span>
            <Download size={12} className="ml-auto text-emerald-600" />
          </button>
        ))}
      </div>
      {exportData && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="export-result">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="font-bold">{exportData.subject.full_name}</div>
              <div className="text-xs text-slate-500">Exported {fmtDateTime(exportData.exported_at)}</div>
            </div>
            <button onClick={download} className="pm-btn-primary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1"
                    data-testid="export-download">
              <FileText size={12} /> Download JSON
            </button>
          </div>
          <div className="text-xs text-slate-600 grid grid-cols-2 md:grid-cols-5 gap-2 font-mono-tab">
            <div>Contracts: <b>{exportData.contracts.length}</b></div>
            <div>Events: <b>{exportData.events.length}</b></div>
            <div>Cases: <b>{exportData.cases.length}</b></div>
            <div>Messages: <b>{exportData.messages.length}</b></div>
            <div>Payroll: <b>{exportData.payroll.length}</b></div>
          </div>
        </div>
      )}
    </div>
  );
}
